import { spawn, execSync, type ChildProcess } from "node:child_process";

const PROCESS_TERMINATION_GRACE_MS = 5_000;

function abortError(): Error {
  const error = new Error("Operation aborted");
  error.name = "AbortError";
  return error;
}

function signalProcessGroup(child: ChildProcess, signal: NodeJS.Signals): void {
  try {
    if (child.pid) process.kill(-child.pid, signal);
    else child.kill(signal);
  } catch {
    try {
      child.kill(signal);
    } catch {
      // The process already exited.
    }
  }
}

export function terminateProcessGroup(
  child: ChildProcess,
  graceMs = PROCESS_TERMINATION_GRACE_MS,
): Promise<void> {
  if (child.exitCode !== null) return Promise.resolve();
  return new Promise((resolve) => {
    let settled = false;
    const finish = (): void => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      child.removeListener("exit", finish);
      resolve();
    };
    const timer = setTimeout(() => {
      signalProcessGroup(child, "SIGKILL");
      finish();
    }, graceMs);
    timer.unref();
    child.once("exit", finish);
    signalProcessGroup(child, "SIGTERM");
  });
}

export function runSetupCommand(
  cmd: string,
  cwd: string,
  onOutput: (stream: "stdout" | "stderr", data: string) => void,
  signal?: AbortSignal,
): Promise<void> {
  if (signal?.aborted) return Promise.reject(abortError());
  return new Promise((resolve, reject) => {
    const child = spawn("sh", ["-c", cmd], {
      cwd,
      stdio: ["ignore", "pipe", "pipe"],
      detached: true,
      env: { ...process.env },
    });
    let settled = false;
    let aborting = false;
    const cleanup = (): void => signal?.removeEventListener("abort", onAbort);
    const settle = (error?: Error): void => {
      if (settled) return;
      settled = true;
      cleanup();
      if (error) reject(error);
      else resolve();
    };
    const onAbort = (): void => {
      if (settled || aborting) return;
      aborting = true;
      void terminateProcessGroup(child).then(() => settle(abortError()));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
    if (signal?.aborted) onAbort();

    child.stdout.on("data", (chunk: Buffer) => {
      if (aborting || signal?.aborted) return;
      onOutput("stdout", chunk.toString());
    });

    child.stderr.on("data", (chunk: Buffer) => {
      if (aborting || signal?.aborted) return;
      onOutput("stderr", chunk.toString());
    });

    child.on("close", (code) => {
      if (aborting) return;
      settle(code === 0 ? undefined : new Error(`Setup command exited with code ${code}`));
    });

    child.on("error", (err) => {
      if (!aborting) settle(err);
    });
  });
}

const AUTH_TOKEN_TIMEOUT_MS = 30_000;

export function runAuthTokenCommand(cmd: string, userEmail: string, cwd: string): string | null {
  try {
    const output = execSync(`${cmd} ${JSON.stringify(userEmail)}`, {
      cwd,
      timeout: AUTH_TOKEN_TIMEOUT_MS,
      stdio: ["ignore", "pipe", "ignore"],
      env: { ...process.env },
    });
    const token = output.toString().trim();
    return token || null;
  } catch {
    return null;
  }
}

export function runStartCommand(
  cmd: string,
  cwd: string,
  onOutput: (stream: "stdout" | "stderr", data: string) => void,
): ChildProcess {
  const child = spawn("sh", ["-c", cmd], {
    cwd,
    stdio: ["ignore", "pipe", "pipe"],
    detached: true,
    env: { ...process.env },
  });

  child.stdout.on("data", (chunk: Buffer) => {
    onOutput("stdout", chunk.toString());
  });

  child.stderr.on("data", (chunk: Buffer) => {
    onOutput("stderr", chunk.toString());
  });

  child.unref();
  return child;
}
